import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  Divider
} from "@mui/material";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import config from "../../config";
const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";

// Default empty row — kept as a constant so addRow/removeRow stay in sync.
// `gst` is the rate (%), `gstAmount` is qty × rate × gst%, `amount` is
// qty × rate (base before GST). Total per row = amount + gstAmount.
const EMPTY_ROW = {
  itemName: "",
  itemId: null,
  qty: "",
  rate: "",
  gst: "",
  amount: 0,
  gstAmount: 0,
};

export default function PurchaseEntry() {
  const token = sessionStorage.getItem("token");

  /* ================= BASIC FORM ================= */
  const [form, setForm] = useState({
    vendorName: "",
    billNo: "",
    billDate: "",
    remark: ""
  });

  /* ================= PAYMENT ================= */
  const [payment, setPayment] = useState({
    mode: "Cash",
    cardnumber: "",
    cardtype: "",
    refno: ""
  });

  /* ================= ITEMS ================= */
  const [items, setItems] = useState([{ ...EMPTY_ROW }]);

  // Master items fetched from the existing rest-items endpoint. Used to power
  // the per-row Autocomplete so users pick from existing master data
  // (auto-fills rate + GST) instead of free-typing every time.
  const [masterItems, setMasterItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchMaster = async () => {
      if (!company?.id || !token) return;
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/home/Getallrestitem/${company.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        setMasterItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load master items:", err);
      }
    };
    fetchMaster();
  }, []);

  // Recomputes amount + gstAmount whenever qty/rate/gst changes. Centralising
  // here so any field edit gives consistent totals without a separate effect.
  const recalcRow = (row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    const gst = parseFloat(row.gst) || 0;
    const amount = qty * rate;
    const gstAmount = (amount * gst) / 100;
    return { ...row, amount, gstAmount };
  };

  const handleItemChange = (i, field, value) => {
    const updated = [...items];
    updated[i] = recalcRow({ ...updated[i], [field]: value });
    setItems(updated);
  };

  // When user picks an item from the master Autocomplete, auto-fill its rate
  // (from price) + GST. Free-typed values are treated as a manual entry —
  // itemId is left null so the backend can decide whether to upsert.
  const handlePickItem = (i, option) => {
    const updated = [...items];
    if (option && typeof option === "object") {
      updated[i] = recalcRow({
        ...updated[i],
        itemName: option.name || "",
        itemId: option.id ?? option.ID ?? null,
        rate:
          updated[i].rate ||
          String(option.price ?? option.itemPrice ?? ""),
        gst:
          updated[i].gst ||
          String(option.gst ?? option.GST ?? ""),
      });
    } else {
      updated[i] = recalcRow({
        ...updated[i],
        itemName: typeof option === "string" ? option : "",
        itemId: null,
      });
    }
    setItems(updated);
  };

  const addRow = () => setItems([...items, { ...EMPTY_ROW }]);

  const removeRow = (i) => {
    if (items.length === 1) {
      // Don't delete the last row — just clear it so the form stays usable.
      setItems([{ ...EMPTY_ROW }]);
      return;
    }
    setItems(items.filter((_, idx) => idx !== i));
  };

  // Totals across all rows. Kept separate from per-row math so the summary
  // card can show a clean breakdown.
  const subtotal = items.reduce((sum, x) => sum + (x.amount || 0), 0);
  const totalGst = items.reduce((sum, x) => sum + (x.gstAmount || 0), 0);
  const totalAmount = subtotal + totalGst;

  /* ================= VALIDATION ================= */
  // Returns the first error message, or null if the form is valid. Centralised
  // so the Save button can short-circuit before hitting the API.
  const getValidationError = () => {
    if (!form.vendorName.trim()) return "Vendor Name is required";
    if (!form.billNo.trim()) return "Bill No is required";
    if (!form.billDate) return "Bill Date is required";
    if (payment.mode === "Card" && !payment.cardnumber.trim())
      return "Card Number is required for Card payment";
    if ((payment.mode === "Online" || payment.mode === "UPI") && !payment.refno.trim())
      return "Reference No is required for Online/UPI payment";

    const validItems = items.filter(
      (r) => r.itemName.trim() && parseFloat(r.qty) > 0 && parseFloat(r.rate) > 0,
    );
    if (validItems.length === 0)
      return "Add at least one item with name, qty and rate";

    // Surface row-level issues — useful so the user knows which line broke.
    for (let idx = 0; idx < items.length; idx++) {
      const r = items[idx];
      const hasAny = r.itemName.trim() || r.qty || r.rate;
      if (!hasAny) continue;
      if (!r.itemName.trim()) return `Row ${idx + 1}: Item Name is required`;
      if (!(parseFloat(r.qty) > 0)) return `Row ${idx + 1}: Qty must be > 0`;
      if (!(parseFloat(r.rate) > 0)) return `Row ${idx + 1}: Rate must be > 0`;
    }
    return null;
  };

  /* ================= SAVE ================= */
  const savePurchase = async () => {
    const err = getValidationError();
    if (err) {
      setSnackbar({ open: true, message: err, severity: "warning" });
      return;
    }

    // Send only the valid rows. Include `amount` as line total INCLUDING GST
    // so the backend's TotalAmount matches what the user saw on screen.
    const cleanItems = items
      .filter((r) => r.itemName.trim() && parseFloat(r.qty) > 0)
      .map((r) => ({
        itemName: r.itemName.trim(),
        itemId: r.itemId || 0,
        qty: parseFloat(r.qty) || 0,
        rate: parseFloat(r.rate) || 0,
        gst: parseFloat(r.gst) || 0,
        amount: (r.amount || 0) + (r.gstAmount || 0),
      }));

    const payload = {
      vendorName: form.vendorName.trim(),
      billNo: form.billNo.trim(),
      billDate: form.billDate,
      remark: form.remark.trim(),
      cardnumber: payment.mode === "Card" ? payment.cardnumber : null,
      cardtype: payment.mode === "Card" ? payment.cardtype : null,
      Companyid: company?.id,
      refno:
        payment.mode === "Online" || payment.mode === "UPI"
          ? payment.refno
          : null,
      items: cleanItems,
    };

    try {
      setSaving(true);
      const res = await fetch(`${config.API_BASE_URL}/home/addAddPurchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      setSnackbar({
        open: true,
        message: "Purchase saved successfully",
        severity: "success",
      });
      // Reset form so user can immediately enter a second purchase without
      // losing vendor context — only the items + bill no clear.
      setForm({ ...form, billNo: "", remark: "" });
      setItems([{ ...EMPTY_ROW }]);
      setPayment({ mode: "Cash", cardnumber: "", cardtype: "", refno: "" });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || "Failed to save purchase",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight="bold" textAlign="center" mb={3}>
        🧾 Purchase Entry
      </Typography>

      {/* ================= HEADER FORM ================= */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Vendor Name"
                fullWidth
                value={form.vendorName}
                onChange={(e) =>
                  setForm({ ...form, vendorName: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Bill No"
                fullWidth
                value={form.billNo}
                onChange={(e) =>
                  setForm({ ...form, billNo: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Bill Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={form.billDate}
                onChange={(e) =>
                  setForm({ ...form, billDate: e.target.value })
                }
              />
            </Grid>

            {/* ================= PAYMENT MODE ================= */}
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Payment Mode"
                fullWidth
                value={payment.mode}
                onChange={(e) =>
                  setPayment({
                    mode: e.target.value,
                    cardnumber: "",
                    cardtype: "",
                    refno: ""
                  })
                }
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
              </TextField>
            </Grid>

            {/* CARD FIELDS */}
            {payment.mode === "Card" && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Card Number"
                    fullWidth
                    value={payment.cardnumber}
                    onChange={(e) =>
                      setPayment({
                        ...payment,
                        cardnumber: e.target.value
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Card Type"
                    fullWidth
                    value={payment.cardtype}
                    onChange={(e) =>
                      setPayment({
                        ...payment,
                        cardtype: e.target.value
                      })
                    }
                  >
                    <MenuItem value="Debit">Debit</MenuItem>
                    <MenuItem value="Credit">Credit</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* ONLINE / UPI */}
            {(payment.mode === "Online" || payment.mode === "UPI") && (
              <Grid item xs={12} md={4}>
                <TextField
                  label="Reference No"
                  fullWidth
                  value={payment.refno}
                  onChange={(e) =>
                    setPayment({ ...payment, refno: e.target.value })
                  }
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </motion.div>

      {/* ================= ITEMS TABLE ================= */}
      <Paper sx={{ mt: 4, borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#0a2540" }}>
                {["Item", "Qty", "Rate", "GST %", "Amount", "GST Amt", "Total", ""].map(
                  (h, i) => (
                    <TableCell key={i} sx={{ color: "#fff", fontWeight: 700 }}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ minWidth: 240 }}>
                    {/* freeSolo lets the user type a name not in the master
                        list (it'll still save as a free-text purchase line). */}
                    <Autocomplete
                      freeSolo
                      options={masterItems}
                      getOptionLabel={(opt) =>
                        typeof opt === "string"
                          ? opt
                          : opt?.name || opt?.itemName || ""
                      }
                      value={row.itemName}
                      onChange={(_, newVal) => handlePickItem(i, newVal)}
                      onInputChange={(_, newInput, reason) => {
                        if (reason === "input")
                          handleItemChange(i, "itemName", newInput);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Search or type item name"
                          fullWidth
                        />
                      )}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 110 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: "any" }}
                      value={row.qty}
                      onChange={(e) =>
                        handleItemChange(i, "qty", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 130 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: "any" }}
                      value={row.rate}
                      onChange={(e) =>
                        handleItemChange(i, "rate", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 110 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ min: 0, max: 100, step: "any" }}
                      value={row.gst}
                      onChange={(e) =>
                        handleItemChange(i, "gst", e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    {Companycurrency}
                    {row.amount.toFixed(2)}
                  </TableCell>

                  <TableCell>
                    {Companycurrency}
                    {row.gstAmount.toFixed(2)}
                  </TableCell>

                  <TableCell sx={{ fontWeight: 700 }}>
                    {Companycurrency}
                    {(row.amount + row.gstAmount).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => removeRow(i)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <Button startIcon={<AddIcon />} onClick={addRow} variant="outlined">
            Add Item
          </Button>
        </Box>
      </Paper>

      {/* ================= SUMMARY ================= */}
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Typography color="text.secondary" fontSize={13}>
              Subtotal
            </Typography>
            <Typography variant="h6">
              {Companycurrency}
              {subtotal.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color="text.secondary" fontSize={13}>
              Total GST
            </Typography>
            <Typography variant="h6">
              {Companycurrency}
              {totalGst.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography color="text.secondary" fontSize={13}>
              Grand Total
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1565c0" }}>
              {Companycurrency}
              {totalAmount.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Remark"
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 2 }}
          value={form.remark}
          onChange={(e) => setForm({ ...form, remark: e.target.value })}
        />

        <Button
          variant="contained"
          size="large"
          sx={{ mt: 3 }}
          onClick={savePurchase}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Purchase"}
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
